import type { SpecialQualifyingStatus } from '@iheartjane/data-access';
import { useGetSpecialQualifyingStatus } from '@iheartjane/data-access/react-query';
import type {
  SmartSortProduct,
  VirtualBudtenderProduct,
} from '@iheartjane/dm-sdk';
import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import {
  SpecialDetailLayout,
  SpecialDetailStatus,
  allowDiscountsOnRequiredProducts,
  getDiscountLabel,
  getDiscountedWithoutSameQualified,
  getPriceIdsForStoreSpecial,
  getSpecialType,
  getStatusText,
} from '@iheartjane/menu';
import { Box, Flex, useMobileMediaQuery } from '@iheartjane/reefer';
import type { Breadcrumb, PriceId } from '@iheartjane/types';
import { useCallback, useEffect } from 'react';
import { Navigate, useLocation, useParams } from 'react-router';

import type { AlgoliaProduct } from '@jane/search/types';
import { buildDiscountableProductsFilter } from '@jane/search/util';
import { MenuBreadcrumbs } from '@jane/shared-ecomm/components';
import { useMenu, useSpecials } from '@jane/shared-ecomm/providers';
/* eslint-disable-next-line no-restricted-imports */
import type { PendingCartProduct } from '@jane/shared/models';
import { useEcommApp } from '@jane/shared/providers';
import type { MenuProduct, StoreSpecial } from '@jane/shared/types';
import { insertIfArr } from '@jane/shared/util';

import { MenuFiltersAndProductsGrid } from '../../MenuComponents/MenuFiltersAndProductsGrid/MenuFiltersAndProductsGrid';
import { MenuPageSeo } from '../../MenuComponents/MenuSeo/MenuPageSeo';
import { MenuTitle } from '../../MenuComponents/MenuTitle/MenuTitle';
import { useComposeMenuFilters } from '../../hooks/useComposeMenuFilters';
import {
  filterProductsByWeight,
  getSpecialFilterAttribute,
  shouldShowWeight,
} from './MenuSpecialDetail.util';
import { MenuSpecialDetailBundleProgress } from './MenuSpecialDetailBundleProgress/MenuSpecialDetailBundleProgress';
import { MenuSpecialDetailHeader } from './MenuSpecialDetailHeader/MenuSpecialDetailHeader';
import { MenuSpecialDetailProgressBar } from './MenuSpecialDetailProgressBar/MenuSpecialDetailProgressBar';

export const MenuSpecialDetail = () => {
  const handleNSW = useFlag(FLAGS.cnSpecialDetailPageNsw);
  const specialsRedesignMenu = useFlag(FLAGS.c3SpecialsRedesignMenu);
  const extendedDealsLabelsEnabled = useFlag(FLAGS.c1ExtendedDealsLabels);

  const { state } = useLocation();
  const { appMode } = useEcommApp();
  const isMobile = useMobileMediaQuery({});
  const { special_id: specialId } = useParams<{
    special_id: string;
  }>();

  const {
    appInfo: { bundlePossibilitiesState },
    cart,
    dispatches: { openCart, deleteCartItem, updateCart },
    paths,
    store,
    userGroupSpecialId,
  } = useMenu();

  const { specials = [], loadingSpecials } = useSpecials();

  const currentSpecial = specials.find(
    (special) => String(special.id) === specialId
  );

  // pass a dummy value of '0' when there is no cart.
  // cart uuid is part of the route, so we get a 404 instead of a valid response if the uuid is missing
  const { data: qualifyingStatusData, refetch: fetchStatus } =
    useGetSpecialQualifyingStatus(
      {
        cartUuid: cart.uuid || '0',
        specialId,
        storeId: store.id,
        userGroupSpecialId,
      },
      { enabled: !!currentSpecial }
    );

  const qualifyingStatus = qualifyingStatusData?.qualifying_status;

  const bundlePossibilities = bundlePossibilitiesState?.bundlePossibilities;
  const filterDiscountableProducts =
    bundlePossibilitiesState?.filterDiscountableProducts ?? false;
  const bundleFilter = buildDiscountableProductsFilter(
    filterDiscountableProducts,
    Array.from(bundlePossibilities?.discountable_product_ids ?? [])
  );

  const storeId = store?.id;

  const { special_type } = currentSpecial || {};
  const isBulk = special_type === 'bulk_pricing';
  const isBundle = special_type === 'bundle';
  const isSpendingThreshold = special_type === 'spending_threshold';
  const isCartTotal = special_type === 'cart_total';
  const isProduct = special_type === 'product';
  const pageTitle = currentSpecial?.title || '';

  const defaultStatusData = {
    qualifying_status: 'not_qualified',
    special: {
      id: specialId,
      description: currentSpecial?.description,
      title: currentSpecial?.title,
      special_type: currentSpecial?.special_type,
      photo: currentSpecial?.photo,
      store_id: storeId,
    },
  };

  useEffect(() => {
    document
      .getElementById('progress-container-scroll-position')
      ?.scrollIntoView({
        block: 'start',
        behavior: 'smooth',
      });

    if (qualifyingStatus === 'applied') {
      openCart();
    }
  }, [qualifyingStatus]);

  useEffect(() => {
    fetchStatus();
  }, [cart.uuid, cart.products]);

  const specialFilter = getSpecialFilterAttribute(
    currentSpecial?.special_type,
    qualifyingStatus
  );

  const applicablePriceIds = getPriceIdsForStoreSpecial(
    currentSpecial,
    qualifyingStatus,
    handleNSW
  );

  const staticFilters = useComposeMenuFilters([
    `${specialFilter}:${currentSpecial?.id}`,
    bundleFilter,
  ]);

  const fromFeatured = state?.breadcrumbs?.some(
    (crumb: Breadcrumb) => crumb.title === 'Featured'
  );

  const specialDetailPath = paths.menuSpecial({
    id: currentSpecial?.id || 0,
    title: pageTitle,
  });

  const breadcrumbs = [
    { url: '/', title: 'Home' },
    { url: paths.menu(), title: store.name },
    ...insertIfArr(!fromFeatured, {
      url: paths.menuCategory('specials'),
      title: store.specials_row_configs?.custom_display_name || 'Specials',
    }),
  ];

  const nextBreadcrumbs = [
    {
      url: paths.menuCategory('specials'),
      title: store.specials_row_configs?.custom_display_name || 'Specials',
    },
    {
      title: pageTitle,
      url: specialDetailPath,
    },
  ];

  const scrollToStatus = () =>
    (document.getElementById('shadow-host')?.shadowRoot || document)
      .getElementById('jane-menu-header')
      ?.scrollIntoView({ behavior: 'smooth' });

  const handleRemoveProduct = (product: {
    id?: number;
    price_id?: PriceId;
  }) => {
    const cartProduct = cart.products.find(
      ({ id, price_id }) => id === product.id && price_id === product.price_id
    );

    if (!cartProduct) return;

    if (cartProduct.count === 1) {
      deleteCartItem(cartProduct.id, cartProduct.price_id);
    } else {
      updateCart(cartProduct.id, cartProduct.price_id, cartProduct.count - 1);
    }
  };

  const {
    calloutSubtext,
    calloutText,
    footerSubtext,
    footerText,
    stickyCallout,
    stickyCalloutSubtext,
    stickyText,
  } = getStatusText({
    currentSpecial,
    qualifyingStatus: qualifyingStatusData,
  });

  const additionalProductFilter = useCallback(
    (
      products:
        | SmartSortProduct<AlgoliaProduct>[]
        | VirtualBudtenderProduct<AlgoliaProduct>[]
    ) => {
      return filterProductsByWeight({
        cartProducts: cart.products as PendingCartProduct[],
        currentSpecial,
        products,
        qualifyingStatus: qualifyingStatusData,
        enableNonStandardWeights: handleNSW,
      });
    },
    [cart.products, currentSpecial, qualifyingStatusData]
  );

  const weightFilter = useCallback(
    (weight: string, product: MenuProduct | AlgoliaProduct) =>
      shouldShowWeight({
        currentSpecial,
        cartProducts: cart.products as PendingCartProduct[],
        qualifyingStatus: qualifyingStatusData,
        showOnlyWeights: applicablePriceIds ?? [],
        enableNonStandardWeights: handleNSW,
      })(weight, product),
    [currentSpecial, cart.products, qualifyingStatusData, applicablePriceIds]
  );

  const allowQualifiersToBeDiscounted =
    allowDiscountsOnRequiredProducts(currentSpecial);

  const discountedWithoutSameQualified =
    getDiscountedWithoutSameQualified(qualifyingStatusData);

  const discountedProductsToShow = allowQualifiersToBeDiscounted
    ? discountedWithoutSameQualified
    : qualifyingStatusData?.discounted_products;

  const maxWeightOption =
    typeof qualifyingStatusData?.target_weight === 'number' &&
    typeof qualifyingStatusData?.product_weight === 'number'
      ? qualifyingStatusData.target_weight - qualifyingStatusData.product_weight
      : null;

  const shouldUseFilters =
    isBulk || isBundle || isProduct || isSpendingThreshold;

  if (!currentSpecial && !loadingSpecials) {
    return <Navigate to={paths.menu()} replace />;
  }

  if (isSpendingThreshold || specialsRedesignMenu) {
    return (
      <Box position="relative">
        <MenuPageSeo path={specialDetailPath} title={pageTitle} />

        <Flex
          flexDirection="column"
          px={isMobile ? 24 : 64}
          pb={isMobile ? 24 : 48}
          gap={isMobile ? 24 : 48}
        >
          {!isMobile && (
            <MenuBreadcrumbs breadcrumbs={breadcrumbs} title={pageTitle} />
          )}

          <SpecialDetailLayout px={0} isLoading={!!loadingSpecials}>
            <SpecialDetailLayout.Banner p={8}>
              <SpecialDetailStatus.Sticky
                calloutText={stickyCallout}
                calloutSubtext={stickyCalloutSubtext}
                text={stickyText}
                progressInGrams={isBulk}
                qualifiedProductsTotal={
                  isBulk
                    ? qualifyingStatusData?.product_weight
                    : qualifyingStatusData?.product_price
                }
                qualifyBySpendAmount={
                  isSpendingThreshold || isCartTotal || isBulk
                }
                qualifyingStatus={qualifyingStatus}
                qualifyingTargetAmount={
                  (isBulk
                    ? qualifyingStatusData?.target_weight
                    : qualifyingStatusData?.qualifying_threshold) ?? 0
                }
                onClick={scrollToStatus}
              />
            </SpecialDetailLayout.Banner>

            <SpecialDetailLayout.Summary
              headerHeight={appMode === 'default' ? 80 : 0}
            >
              <SpecialDetailStatus>
                <SpecialDetailStatus.Header
                  headerImageSrc={
                    currentSpecial?.photo?.urls?.original ?? undefined
                  }
                  specialDescription={currentSpecial?.description || ''}
                  specialTitle={currentSpecial?.title || ''}
                  specialType={
                    currentSpecial
                      ? getSpecialType(
                          currentSpecial?.special_type,
                          store?.store_compliance_language_settings,
                          extendedDealsLabelsEnabled
                        )
                      : ''
                  }
                  specialTerms={currentSpecial?.terms || ''}
                />
                {!isProduct && (
                  <SpecialDetailStatus.BundleProgress
                    allowQualifiersToBeDiscounted={
                      allowQualifiersToBeDiscounted
                    }
                    calloutSubtext={calloutSubtext}
                    calloutText={calloutText || ''}
                    discountedProducts={discountedProductsToShow || []}
                    isGold={false}
                    onRemoveProduct={(product) => handleRemoveProduct(product)}
                    progressInGrams={isBulk}
                    qualifiedProducts={
                      qualifyingStatusData?.qualified_products || []
                    }
                    qualifiedProductsTotal={
                      isBulk
                        ? qualifyingStatusData?.product_weight
                        : qualifyingStatusData?.product_price
                    }
                    qualifyBySpendAmount={
                      isSpendingThreshold || isCartTotal || isBulk
                    }
                    qualifyingStatus={qualifyingStatus || 'not_qualified'}
                    qualifyingTargetAmount={
                      (isBulk
                        ? qualifyingStatusData?.target_weight
                        : qualifyingStatusData?.qualifying_threshold) ?? 0
                    }
                    tagLabel={
                      currentSpecial
                        ? getDiscountLabel({
                            discountAmount: currentSpecial.discount_amount,
                            discountType: currentSpecial.discount_type,
                            specialType: currentSpecial.special_type,
                            storeComplianceLanguageSettings:
                              store?.store_compliance_language_settings,
                          })
                        : ''
                    }
                  />
                )}
                {!isProduct && (
                  <SpecialDetailStatus.Footer
                    footerText={footerText}
                    footerSubtext={footerSubtext}
                    onCheckoutClick={() => openCart()}
                    qualifyingStatus={qualifyingStatus || 'not_qualified'}
                  />
                )}
              </SpecialDetailStatus>
            </SpecialDetailLayout.Summary>

            {currentSpecial && (
              <SpecialDetailLayout.Content>
                <MenuTitle />
                <MenuFiltersAndProductsGrid
                  {...(shouldUseFilters && {
                    additionalProductFilter,
                    maxWeightOption,
                    weightFilter,
                  })}
                  breadcrumbs={nextBreadcrumbs}
                  menuCategory="specials"
                  noPadding
                  pageTitle={pageTitle}
                  showOnlyWeights={applicablePriceIds}
                  staticFilters={staticFilters}
                  withoutDrawerOnAddToCart
                />
              </SpecialDetailLayout.Content>
            )}
          </SpecialDetailLayout>
        </Flex>
      </Box>
    );
  }

  return (
    <Box>
      <MenuPageSeo path={specialDetailPath} title={pageTitle} />
      <Flex
        flexDirection="column"
        px={isMobile ? 0 : 64}
        pb={isMobile ? 24 : 48}
        gap={isMobile ? 0 : 24}
      >
        {!isMobile && (
          <MenuBreadcrumbs
            breadcrumbs={breadcrumbs}
            title={pageTitle}
            pb={24}
          />
        )}

        {currentSpecial && <MenuSpecialDetailHeader special={currentSpecial} />}

        {(isBulk || isCartTotal) && (
          <MenuSpecialDetailProgressBar
            status={
              (qualifyingStatusData ||
                defaultStatusData) as SpecialQualifyingStatus
            }
            special={currentSpecial as StoreSpecial}
          />
        )}

        {isBundle && (
          <MenuSpecialDetailBundleProgress
            status={
              (qualifyingStatusData ||
                defaultStatusData) as SpecialQualifyingStatus
            }
            special={currentSpecial as StoreSpecial}
          />
        )}
      </Flex>

      {currentSpecial && (
        <Box>
          <MenuTitle px={isMobile ? 24 : 64} />
          <MenuFiltersAndProductsGrid
            {...(shouldUseFilters && {
              additionalProductFilter,
              maxWeightOption,
              weightFilter,
            })}
            breadcrumbs={nextBreadcrumbs}
            menuCategory="specials"
            pageTitle={pageTitle}
            showOnlyWeights={applicablePriceIds}
            staticFilters={staticFilters}
            withoutDrawerOnAddToCart
          />
        </Box>
      )}
    </Box>
  );
};
